import { ROUTE_META } from "@/seo/route-meta";
import { MONO } from "@/components/home-a/theme";
import { ScoreBadge } from "@/components/home-a/Parts";
import { Capture, MOBILE_MARKERS } from "@/components/home-a/StorePage";
import { GrowBar } from "@/components/home-a/Motion";
import {
  ActionLink, CardGrid, CheckList, CompareTable, CtaBand, FaqList, Label, PageHero, Panel,
  Reveal, RevealGroup, Section, SectionHead, SitePage, Steps, useTr, type Tr,
} from "@/components/site/Kit";
import { LeadCapture } from "@/components/site/LeadPage";
import { cn } from "@/lib/utils";

const PATH = "/agencies";

/** A report cover in an agency's own colours. */
function Cover({ name, accent, tone, className }: { name: string; accent: string; tone: string; className?: string }) {
  return (
    <div className={cn("relative h-[260px] w-[190px] overflow-hidden rounded-[10px] border border-foreground/10 p-4 shadow-[0_30px_60px_rgba(0,0,0,0.55)]", className)} style={{ background: tone }}>
      <div className="flex items-center gap-1.5">
        <span className="h-4 w-4 rounded" style={{ background: accent }} />
        <span className="text-[10px] font-bold tracking-[0.16em] text-white/90">{name}</span>
      </div>
      <p className="mt-10 text-[9px] uppercase tracking-[0.18em] text-white/50">CRO audit</p>
      <p className="mt-1 text-[16px] font-semibold leading-tight text-white">kyma.store<br />Product page</p>
      <div className="mt-4 h-[3px] w-10 rounded" style={{ background: accent }} />
      <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
        <span className="text-[26px] font-semibold text-white" style={{ fontFamily: MONO }}>64</span>
        <span className="text-[8.5px] text-white/50">Prepared by {name.toLowerCase()}</span>
      </div>
    </div>
  );
}

function ClientsBoard({ tr }: { tr: Tr }) {
  const rows: [string, string, number, number][] = [
    ["KYMA", "kyma.store", 64, 81],
    ["HALDEN", "halden.co", 52, 78],
    ["NORD", "nord-home.gr", 71, 84],
    ["LUMA", "luma.clinic", 58, 72],
  ];
  return (
    <div className="rounded-[18px] border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <Label>{tr("Πελάτες", "Clients")}</Label>
        <span className="text-[11px] text-[hsl(var(--ec-muted))]" style={{ fontFamily: MONO }}>4</span>
      </div>
      <ul className="mt-3 divide-y divide-border">
        {rows.map(([n, d, s, r], i) => (
          <li key={n} className="grid grid-cols-[30px_minmax(0,1fr)_minmax(0,1fr)_34px] items-center gap-3 py-2.5">
            <span className="grid h-7 w-7 place-items-center rounded-lg bg-foreground/[0.06] text-[11px] font-semibold">{n[0]}</span>
            <span className="min-w-0">
              <span className="block truncate text-[13.5px] font-medium">{n}</span>
              <span className="block truncate text-[11px] text-[hsl(var(--ec-muted))]" style={{ fontFamily: MONO }}>{d}</span>
            </span>
            <span className="relative block">
              <GrowBar pct={r} className="h-1.5" barClassName="bg-white/15" delay={i * 0.06} />
              <span className="absolute inset-0"><GrowBar pct={s} className="h-1.5 bg-transparent" barClassName="bg-[hsl(var(--ec-mint))]" delay={i * 0.06 + 0.1} /></span>
            </span>
            <span className="text-right text-[12px]" style={{ fontFamily: MONO }}>{s}</span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[11px] text-[hsl(var(--ec-muted))]">{tr("Σκορ σήμερα · πού μπορεί να φτάσει", "Score today · where it can reach")}</p>
    </div>
  );
}

export default function Agencies() {
  const { tr, isEl } = useTr();
  const meta = ROUTE_META[PATH];

  const faqsEl = [
    { q: "Τι σημαίνει white label στο EyeCaptain;", a: "Τα PDF, τα links κοινοποίησης και τα embeds βγαίνουν με το δικό σας λογότυπο και χρώματα, και μπορείτε να ορίσετε παλέτα ανά πελάτη. Το EyeCaptain δεν εμφανίζεται στο report." },
    { q: "Μπορεί να δουλεύει όλη η ομάδα στον ίδιο χώρο;", a: "Ναι. Το workspace έχει μέλη με ρόλους, κοινά credits και φακέλους πελατών με όλες τις αναλύσεις του καθενός." },
    { q: "Χρειάζομαι CRO specialist για να το χρησιμοποιήσω;", a: "Όχι. Το report εξηγεί κάθε εύρημα και την αλλαγή που προτείνει, με πριν και μετά, ώστε να το παρουσιάσει οποιοσδήποτε στην ομάδα." },
    { q: "Ποιο πλάνο χρειάζομαι;", a: "Δείτε τη σελίδα τιμών για το ποιο πλάνο περιλαμβάνει white label και workspace ομάδας, ή γράψτε μας για προσαρμοσμένο πλάνο." },
  ];
  const faqs = isEl ? faqsEl : meta?.faqs ?? [];

  return (
    <SitePage related="agencies">
      <PageHero
        crumbs={[{ label: tr("Αρχική", "Home"), to: "/" }, { label: "Agencies" }]}
        kicker={tr("Για agencies", "For agencies")}
        title={isEl ? "Εργαλείο CRO audit με τη δική σας επωνυμία, για agencies" : meta.h1}
        lead={
          isEl
            ? "Δώστε πλήρη UX και CRO ανάλυση σε κάθε πελάτη σε λεπτά, με το δικό σας λογότυπο. Φάκελοι πελατών, PDF με τα χρώματά σας, και πλάνα φτιαγμένα για ομάδες. Χωρίς CRO specialist στο γραφείο."
            : meta.intro
        }
        visual={
          <div className="relative mx-auto flex h-[320px] w-full max-w-[480px] items-center justify-center">
            <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_60%_55%_at_50%_50%,hsl(var(--primary)/0.2),transparent_70%)]" />
            <Cover name="NORTHWAVE" accent="#f2a93b" tone="linear-gradient(160deg,#1d2440,#0f1426)" className="absolute left-[4%] top-[30px] -rotate-[8deg]" />
            <Cover name="STUDIO OAK" accent="#e36b5b" tone="linear-gradient(160deg,#2a1f1b,#15100e)" className="absolute right-[4%] top-[30px] rotate-[7deg]" />
            <Cover name="YOUR AGENCY" accent="#36b588" tone="linear-gradient(160deg,#0c2a22,#07130f)" className="ec-float relative z-10" />
          </div>
        }
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <ActionLink to="/free-audit">{tr("Δοκιμάστε σε πελάτη, δωρεάν", "Try it on a client, free")}</ActionLink>
          <ActionLink to="/contact" variant="white">{tr("Μιλήστε μαζί μας", "Talk to us")}</ActionLink>
        </div>
      </PageHero>

      <Section>
        <SectionHead
          idx="01"
          label={tr("Πού το χρησιμοποιούν", "Where agencies use it")}
          title={tr("Τρεις στιγμές που ένα audit κερδίζει πελάτη", "Three moments an audit wins you a client")}
        />
        <Steps
          steps={[
            {
              title: tr("Στο pitch", "In the pitch"),
              body: tr("Φέρτε στην πρώτη συνάντηση ένα report για τη σελίδα του υποψήφιου πελάτη. Δείχνετε τι θα αλλάξετε, όχι τι μπορείτε να κάνετε.", "Bring a report on the prospect's own page to the first meeting. You show what you would change, not what you are capable of."),
              visual: <div className="flex h-full items-center justify-center gap-3 p-4"><Capture scale={0.24} cropH={640} markers={MOBILE_MARKERS} radius={8} /><ScoreBadge size={88} score={64} reach={81} /></div>,
            },
            {
              title: tr("Στο onboarding", "At onboarding"),
              body: tr("Ένα audit αναφοράς στην αρχή: από πού ξεκινάτε, τι διορθώνετε πρώτο, με ποια σειρά.", "A baseline audit on day one: where you start, what you fix first, in what order."),
              visual: <div className="flex h-full items-center justify-center p-4"><LeadCapture scale={0.24} cropH={740} radius={8} /></div>,
            },
            {
              title: tr("Κάθε μήνα", "Every month"),
              body: tr("Ξανατρέχετε την ανάλυση και δείχνετε τι άλλαξε. Το report του μήνα είναι έτοιμο πριν από τη συνάντηση.", "Re-run the audit and show what moved. The monthly report is ready before the meeting."),
              visual: (
                <div className="flex h-full flex-col justify-center gap-2 p-5">
                  {[["Jul", 52], ["Aug", 58], ["Sep", 64], ["Oct", 71]].map(([m, v], i) => (
                    <div key={String(m)} className="grid grid-cols-[30px_minmax(0,1fr)_24px] items-center gap-2 text-[11.5px]">
                      <span className="text-[hsl(var(--ec-muted))]" style={{ fontFamily: MONO }}>{m}</span>
                      <GrowBar pct={Number(v)} className="h-1.5" barClassName={i === 3 ? "bg-[hsl(var(--ec-mint))]" : "bg-white/35"} delay={i * 0.1} />
                      <span className="text-right" style={{ fontFamily: MONO }}>{v}</span>
                    </div>
                  ))}
                </div>
              ),
            },
          ]}
        />
      </Section>

      <Section>
        <SectionHead
          idx="02"
          label="White label"
          title={tr("Το report είναι δικό σας", "The report is yours")}
          lead={tr("Λογότυπο, χρώματα, εξώφυλλο και links κοινοποίησης με την επωνυμία σας. Παλέτα ανά πελάτη όπου χρειάζεται.", "Your logo, colours, cover and share links. A palette per client where you need one.")}
        />
        <CardGrid
          cols={4}
          items={[
            { title: tr("PDF με την επωνυμία σας", "PDF under your brand"), body: tr("Εξώφυλλο, χρώματα, συντάκτης και σήμανση εμπιστευτικότητας.", "Cover, colours, author and a confidential mark.") },
            { title: tr("Links και embeds", "Share links and embeds"), body: tr("Ο πελάτης βλέπει το report στη δική σας επωνυμία.", "Clients open the report under your brand.") },
            { title: tr("Χρώματα ανά πελάτη", "Colours per client"), body: tr("Το PDF παίρνει την παλέτα του πελάτη όταν το ζητάτε.", "The PDF takes the client's palette when you want it to.") },
            { title: tr("Αυτόματη ταυτότητα", "Brand auto-fill"), body: tr("Λογότυπο και χρώματα διαβάζονται από το site σας.", "Logo and colours are read from your website.") },
          ]}
        />
      </Section>

      <Section>
        <SectionHead
          idx="03"
          label={tr("Workspace", "Workspace")}
          title={tr("Όλοι οι πελάτες σε έναν χώρο", "Every client in one place")}
          lead={tr("Φάκελος ανά πελάτη με όλες τις αναλύσεις, ανταγωνιστές και ιστορικό. Η ομάδα δουλεύει με κοινά credits.", "A folder per client with every audit, their competitors and the history. The team works from shared credits.")}
        />
        <div className="mt-12 grid grid-cols-1 gap-3.5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <Reveal><ClientsBoard tr={tr} /></Reveal>
          <Reveal>
            <Panel className="p-6">
              <CheckList
                items={[
                  tr("Φάκελοι πελατών με domains και ανταγωνιστές", "Client folders with domains and competitors"),
                  tr("Μέλη ομάδας με ρόλους", "Team members with roles"),
                  tr("Κοινά credits για όλη την ομάδα", "Shared credits for the whole team"),
                  tr("Ιστορικό σκορ ανά σελίδα", "Score history per page"),
                  tr("Σύγκριση με έως τρεις ανταγωνιστές", "Comparison with up to three competitors"),
                ]}
              />
            </Panel>
          </Reveal>
        </div>
      </Section>

      <Section>
        <SectionHead idx="04" label={tr("Σύγκριση", "Comparison")} title={tr("Δεν χρειάζεστε CRO specialist όταν έχετε CRO agent", "You do not need a CRO specialist when you have a CRO agent")} />
        <CompareTable
          head={["", "EyeCaptain", tr("CRO σύμβουλος", "CRO consultant")]}
          rows={[
            [tr("Κόστος", "Cost"), tr("από $59/μήνα", "from $59/mo"), tr("$3.000+/μήνα", "$3,000+/mo")],
            [tr("Χρόνος", "Turnaround"), tr("λεπτά", "minutes"), tr("εβδομάδες", "weeks")],
            [tr("Κλίμακα", "Scale"), tr("κάθε πελάτης, κάθε μήνα", "every client, every month"), tr("2 έως 3 έργα", "2 to 3 projects")],
            [tr("Επωνυμία", "Branding"), tr("η δική σας", "yours"), tr("του συμβούλου", "the consultant's")],
            [tr("Διαθεσιμότητα", "Availability"), "24/7", tr("ωράριο γραφείου", "office hours")],
          ]}
        />
      </Section>

      <Section>
        <SectionHead idx="05" label={tr("Ερωτήσεις", "Questions")} title={tr("Συχνές ερωτήσεις από agencies", "Common questions from agencies")} />
        <FaqList items={faqs} />
      </Section>

      <CtaBand
        title={tr("Τρέξτε ένα audit στον επόμενο πελάτη σας", "Run an audit on your next client")}
        body={tr("Δωρεάν, χωρίς κάρτα. Αν σας πείσει, δείτε τα πλάνα για ομάδες.", "Free, no card. If it convinces you, see the plans for teams.")}
      >
        <RevealGroup className="mt-4">
          <Reveal>
            <a href="/pricing" className="text-[14px] text-[hsl(var(--ec-mint))] underline decoration-[hsl(var(--ec-mint)/0.35)] underline-offset-4">{tr("Δείτε τα πλάνα", "See plans")}</a>
          </Reveal>
        </RevealGroup>
      </CtaBand>
    </SitePage>
  );
}
